/**
 * Operator-facing hero copy. Never paint raw `state=PARKED`.
 */

import { historyAmd } from "./historyOutcome";
import type { CallState } from "./types";

const STATE_COPY: Record<CallState, string> = {
  IDLE: "Ready",
  ORIGINATING: "Dialing…",
  CLASSIFYING: "Listening…",
  ACCEPT_GATE: "Waiting for 1 or 2",
  COLLECTING: "Collecting code",
  PARKED: "Parked — decide",
  PLAYING: "Playing prompt",
  HANGING_UP: "Hanging up",
  DONE: "Call ended",
};

const CLASSIFYING_DETAIL: Record<string, string> = {
  settle: "Connecting…",
  initial: "Listening…",
  identity: "Identifying…",
  post_screen: "Waiting for answer…",
  hello_settle: "Connecting…",
};

const PLAYING_DETAIL: Record<string, string> = {
  send_otp: "SEND CODE NOW — press O when sent",
};

export type HeroTone = "ready" | "live" | "parked" | "failed";

export const AMD_EMOJI: Record<string, string> = {
  HUMAN: "👤",
  MACHINE: "🤖",
  SCREENING: "📱",
  UNKNOWN: "❓",
};

export const PHASE_EMOJI: Record<string, string> = {
  ORIGINATING: "📞",
  CLASSIFYING: "👂",
  COLLECTING: "🔢",
  PARKED: "🅿️",
  DONE: "✅",
};

/**
 * Dial header/hero emoji: raw amd_label when set, else call phase.
 * Canonical enum only. Do not pass a History far-end overlay.
 */
export function consoleEmoji(state: string, amd?: string | null): string {
  const label = historyAmd(amd);
  if (label && AMD_EMOJI[label]) return AMD_EMOJI[label];
  return PHASE_EMOJI[state] ?? "";
}

/** Status-strip copy. Never the raw CLASSIFYING enum. */
export function operatorStepLine(state: string, detail?: string | null): string | null {
  const phase = (detail ?? "").trim();
  const stem = phase.endsWith(".txt") ? phase.slice(0, -4) : phase;
  switch (state) {
    case "ORIGINATING":
      return "Dialing";
    case "CLASSIFYING":
      if (stem === "identity") return "Screening";
      if (stem === "post_screen") return "Waiting connect";
      if (stem === "reintroduce" || stem === "screening_response") return "Reintroduce";
      if (stem === "settle" || stem === "hello_settle") return "Connecting";
      return "Listening";
    case "ACCEPT_GATE":
      return "Press 1 or 2";
    case "PLAYING":
      if (stem === "identification") return "Press 1 or 2";
      if (stem === "send_code") return "Playing send-code";
      if (stem === "send_otp") return "SEND CODE NOW — press O when sent";
      if (stem === "screening_response" || stem === "reintroduce") return "Reintroduce";
      return "Playing prompt";
    case "COLLECTING":
      if (stem === "retry") return "Retry code";
      return "Enter code";
    case "PARKED":
      return "Parked — A/R/S";
    case "DONE":
      return "Call ended";
    case "HANGING_UP":
      return "Hanging up";
    default:
      return null;
  }
}

/** Terminal status phase. heroCopy, else step line — never snapshot.state. */
export function consoleStatusText(snapshot: {
  state: CallState;
  detail?: string | null;
  heroCopy?: string | null;
}): string {
  const hero = (snapshot.heroCopy || "").trim();
  if (hero && !/\bCLASSIFYING\b/.test(hero)) return hero;
  return operatorStepLine(snapshot.state, snapshot.detail) || heroCopy(snapshot.state, snapshot.detail);
}

/**
 * Return human copy for the live-call hero.
 */
export function heroCopy(state: CallState, detail: string | null | undefined): string {
  if (state === "CLASSIFYING") {
    if (!detail) return "Listening…";
    return CLASSIFYING_DETAIL[detail] ?? "Listening…";
  }
  if (state === "PLAYING" && detail) {
    const playing = PLAYING_DETAIL[detail];
    if (playing) return playing;
  }
  if (state === "PARKED") {
    return "Parked — decide";
  }
  return STATE_COPY[state] ?? "Ready";
}

/**
 * Visual tone for the hero block.
 */
export function heroTone(
  state: CallState,
  hangupCause: string | null | undefined,
  abnormal: boolean,
): HeroTone {
  if (state === "PARKED") return "parked";
  if (state === "IDLE") return "ready";
  if (state === "DONE") {
    if (abnormal || hangupCause) {
      const cause = (hangupCause ?? "").toUpperCase();
      if (
        cause &&
        cause !== "NORMAL_CLEARING" &&
        cause !== "NORMAL" &&
        cause !== "SUCCESS"
      ) {
        return "failed";
      }
      if (abnormal) return "failed";
      return "ready";
    }
    return "ready";
  }
  return "live";
}

/**
 * Format remaining park seconds as `m:ss`.
 */
export function formatPark(remainingS: number | null | undefined): string {
  if (remainingS === null || remainingS === undefined) return "—";
  const total = Math.max(0, Math.floor(remainingS));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Display a party field or an em dash.
 */
export function field(value: string | null | undefined): string {
  if (!value) return "—";
  const text = value.trim();
  return text || "—";
}
